<template>
  <section class="atlas-page">
    <div class="atlas-shell">
      <div class="topbar">
        <div class="crumbs">
          <button type="button" class="nav-chip" @click="goPortal">{{ t("portal") }}</button>
          <button type="button" class="nav-chip subtle" @click="goKnowledge">{{ t("knowledge") }}</button>
        </div>
        <div class="lang-pill">
          <button
            v-for="option in languages"
            :key="option"
            type="button"
            :class="['lang-chip', { active: option === lang }]"
            @click="setLang(option)"
          >
            {{ option.toUpperCase() }}
          </button>
        </div>
      </div>

      <p v-if="loading" class="state-card">{{ t("loading") }}</p>
      <p v-else-if="errorMessage" class="error-panel">{{ errorMessage }}</p>

      <template v-else-if="technique">
        <header class="hero-card panel-card">
          <div class="hero-copy">
            <p class="eyebrow">{{ t("eyebrow") }}</p>
            <div class="hero-topline">
              <span class="soft-badge">{{ getCategoryLabel(technique.category, lang) }}</span>
              <span class="status-chip">{{ getStatusLabel(technique.status, lang) }}</span>
            </div>
            <h1>{{ pickLangText(technique.item, lang) }}</h1>
            <p class="lead">{{ selectedVersionLead }}</p>
            <div class="chip-row">
              <span v-for="tag in technique.tags || []" :key="tag" class="ink-chip">{{ tag }}</span>
            </div>
          </div>

          <aside class="hero-meta">
            <div class="meta-box">
              <span class="meta-label">{{ t("currentVersion") }}</span>
              <strong>v{{ selectedVersion?.versionNo ?? technique.currentVersionNo ?? 1 }}</strong>
            </div>
            <div class="meta-box">
              <span class="meta-label">{{ t("effectiveDate") }}</span>
              <strong>{{ formatDateOnly(selectedVersion?.effectiveDate || technique.effectiveDate) }}</strong>
            </div>
            <div class="meta-box">
              <span class="meta-label">{{ t("versionCount") }}</span>
              <strong>{{ technique.versions?.length || 0 }}</strong>
            </div>
            <div class="meta-box wide">
              <span class="meta-label">Slug</span>
              <strong>{{ technique.slug }}</strong>
            </div>
          </aside>
        </header>

        <section v-if="youtubeIds.length || bilibiliIds.length" class="panel-card media-card">
          <div class="section-head">
            <div>
              <p class="section-tag">{{ t("mediaTag") }}</p>
              <h2>{{ t("videos") }}</h2>
            </div>
            <span>{{ t("videoHint") }}</span>
          </div>
          <div class="video-grid">
            <div v-for="videoId in youtubeIds" :key="`yt-${videoId}`" class="video-frame">
              <iframe :src="youtubeEmbedUrl(videoId)" allowfullscreen loading="lazy"></iframe>
            </div>
            <div v-for="videoId in bilibiliIds" :key="`bi-${videoId}`" class="video-frame">
              <iframe :src="bilibiliEmbedUrl(videoId)" allowfullscreen loading="lazy"></iframe>
            </div>
          </div>
        </section>

        <section class="split-stage">
          <article class="panel-card version-card-wrap">
            <div class="section-head">
              <div>
                <p class="section-tag">{{ t("versionsTag") }}</p>
                <h2>{{ t("versions") }}</h2>
              </div>
              <span>{{ technique.versions?.length || 0 }} {{ t("items") }}</span>
            </div>

            <div class="version-tabs">
              <button
                v-for="version in technique.versions || []"
                :key="version.techniqueVersionId"
                type="button"
                :class="['version-tab', { active: version.techniqueVersionId === selectedVersion?.techniqueVersionId }]"
                @click="selectedVersionId = version.techniqueVersionId"
              >
                <span>v{{ version.versionNo }}</span>
                <small>{{ formatDateOnly(version.effectiveDate) }}</small>
              </button>
            </div>

            <div v-if="selectedVersion" class="section-list">
              <article
                v-for="block in versionBlocks"
                :key="block.key"
                class="section-card"
              >
                <div class="hero-topline">
                  <span class="soft-badge">{{ block.label }}</span>
                  <span v-if="block.key === 'revision'" class="status-chip">
                    {{ selectedVersion.isCurrent ? t("current") : t("past") }}
                  </span>
                </div>
                <p>{{ block.text }}</p>
              </article>
            </div>
          </article>

          <article class="panel-card term-card-wrap">
            <div class="section-head">
              <div>
                <p class="section-tag">{{ t("termsTag") }}</p>
                <h2>{{ t("relatedTerms") }}</h2>
              </div>
              <span>{{ technique.relatedTerms?.length || 0 }} {{ t("items") }}</span>
            </div>

            <div v-if="!technique.relatedTerms?.length" class="empty-state">
              {{ t("emptyTerms") }}
            </div>

            <div v-else class="term-list">
              <article
                v-for="item in technique.relatedTerms"
                :key="item.techniqueTermId"
                class="term-card clickable-card"
                @click="openTerm(item.term.termId)"
              >
                <div class="hero-topline">
                  <span class="soft-badge">{{ item.term.term }}</span>
                  <span class="status-chip">{{ getRelationLabel(item.relationType, lang) }}</span>
                </div>
                <h3>{{ pickLangText(item.term.label, lang) }}</h3>
                <p class="term-pron">{{ item.term.katakana || item.term.romaji || item.term.py || '-' }}</p>
                <p>{{ summarizeText(item.term.description, lang, 160) }}</p>
              </article>
            </div>
          </article>
        </section>
      </template>
    </div>
  </section>
</template>

<script setup>
import axios from "axios";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  API_BASE,
  ensureApiObject,
  bilibiliEmbedUrl,
  extractMediaIds,
  formatDateOnly,
  getCategoryLabel,
  getRelationLabel,
  getStatusLabel,
  pickLangText,
  summarizeText,
  youtubeEmbedUrl,
} from "./portalShared";
import { useSiteLanguage } from "../../store/siteLanguage";

const route = useRoute();
const router = useRouter();
const { lang, languages, setLang } = useSiteLanguage();

const copy = {
  ja: {
    portal: "ポータル",
    knowledge: "知識一覧",
    loading: "技術詳細を読込中...",
    eyebrow: "PRACTICE LEDGER",
    currentVersion: "現在版",
    effectiveDate: "反映日",
    versionCount: "版数",
    current: "現行",
    past: "旧版",
    mediaTag: "VIDEO REVIEW",
    videos: "Videos",
    videoHint: "この版に紐づく動画を確認。",
    versionsTag: "VERSION TRACK",
    versions: "Versions",
    termsTag: "TERM CONNECTIONS",
    relatedTerms: "Related Terms",
    items: "件",
    emptyTerms: "この技術に関連術語はまだありません。",
    revision: "版の意図",
    form: "形",
    principle: "理",
    application: "用",
    practice: "行",
    level: "対象",
  },
  zh: {
    portal: "门户",
    knowledge: "知识列表",
    loading: "正在读取技术详情...",
    eyebrow: "PRACTICE LEDGER",
    currentVersion: "当前版本",
    effectiveDate: "生效日",
    versionCount: "版本数",
    current: "现行",
    past: "旧版",
    mediaTag: "VIDEO REVIEW",
    videos: "Videos",
    videoHint: "查看这版对应的视频。",
    versionsTag: "VERSION TRACK",
    versions: "Versions",
    termsTag: "TERM CONNECTIONS",
    relatedTerms: "Related Terms",
    items: "条",
    emptyTerms: "这项技术还没有关联术语。",
    revision: "版本意图",
    form: "形",
    principle: "理",
    application: "用",
    practice: "行",
    level: "对象",
  },
  en: {
    portal: "Portal",
    knowledge: "Knowledge",
    loading: "Loading technique detail...",
    eyebrow: "PRACTICE LEDGER",
    currentVersion: "Current version",
    effectiveDate: "Effective date",
    versionCount: "Versions",
    current: "Current",
    past: "Past",
    mediaTag: "VIDEO REVIEW",
    videos: "Videos",
    videoHint: "Media linked to this version.",
    versionsTag: "VERSION TRACK",
    versions: "Versions",
    termsTag: "TERM CONNECTIONS",
    relatedTerms: "Related Terms",
    items: "items",
    emptyTerms: "This technique has no related terms yet.",
    revision: "Revision note",
    form: "Form",
    principle: "Principle",
    application: "Application",
    practice: "Practice",
    level: "Level",
  },
};

function t(key) {
  return copy[lang.value]?.[key] || key;
}

const technique = ref(null);
const selectedVersionId = ref("");
const loading = ref(true);
const errorMessage = ref("");

const selectedVersion = computed(() => {
  const versions = technique.value?.versions || [];
  if (!versions.length) {
    return null;
  }

  return (
    versions.find((entry) => entry.techniqueVersionId === selectedVersionId.value) ||
    versions.find((entry) => entry.isCurrent) ||
    versions[0]
  );
});

const selectedVersionLead = computed(() => pickLangText(selectedVersion.value?.revisionNote, lang.value, "-"));
const youtubeIds = computed(() => extractMediaIds(selectedVersion.value?.youtubeVideoId, lang.value));
const bilibiliIds = computed(() => extractMediaIds(selectedVersion.value?.bilibiliVideoId, lang.value));
const versionBlocks = computed(() => {
  if (!selectedVersion.value) {
    return [];
  }

  return [
    { key: "revision", label: t("revision"), text: pickLangText(selectedVersion.value.revisionNote, lang.value) },
    { key: "form", label: t("form"), text: pickLangText(selectedVersion.value.form, lang.value) },
    { key: "principle", label: t("principle"), text: pickLangText(selectedVersion.value.principle, lang.value) },
    { key: "application", label: t("application"), text: pickLangText(selectedVersion.value.application, lang.value) },
    { key: "practice", label: t("practice"), text: pickLangText(selectedVersion.value.practice, lang.value) },
    { key: "level", label: t("level"), text: pickLangText(selectedVersion.value.level, lang.value) },
  ];
});

async function loadTechnique() {
  loading.value = true;
  errorMessage.value = "";

  try {
    const response = await axios.get(`${API_BASE}/api/kendou/techniques/${encodeURIComponent(route.params.slug)}`, {
      timeout: 10000,
    });
    const data = ensureApiObject(response, "Kendou technique API");
    technique.value = data;
    selectedVersionId.value =
      data?.versions?.find((entry) => entry.isCurrent)?.techniqueVersionId ||
      data?.versions?.[0]?.techniqueVersionId ||
      "";
  } catch (error) {
    console.error(error);
    technique.value = null;
    selectedVersionId.value = "";
    errorMessage.value = error?.response?.status === 404 ? "Technique not found." : error?.message || "Failed to load technique.";
  } finally {
    loading.value = false;
  }
}

function goPortal() {
  router.push("/KendouPage/terminology");
}

function goKnowledge() {
  router.push("/KendouPage/terminology?section=techniques&view=knowledge");
}

function openTerm(termId) {
  router.push(`/KendouPage/term/${encodeURIComponent(termId)}`);
}

watch(() => route.params.slug, loadTechnique);
onMounted(loadTechnique);
</script>

<style scoped>
.atlas-page {
  --paper: rgba(251, 245, 235, 0.92);
  --paper-strong: rgba(255, 250, 242, 0.98);
  --ink: #2f2116;
  --ink-soft: #5d4b3d;
  --line: rgba(182, 148, 110, 0.34);
  min-height: 100vh;
  padding: 20px;
  background:
    radial-gradient(circle at 16% 12%, rgba(255, 255, 255, 0.7), transparent 28%),
    radial-gradient(circle at 86% 82%, rgba(235, 190, 116, 0.28), transparent 26%),
    linear-gradient(135deg, #e7d8bf 0%, #efe6d3 24%, #f5eedf 48%, #eadab8 100%);
}

.atlas-shell { max-width: 1380px; margin: 0 auto; display: grid; gap: 18px; }
.topbar, .crumbs, .hero-topline, .chip-row, .section-head { display: flex; gap: 10px; flex-wrap: wrap; justify-content: space-between; }
.lang-pill { display: inline-flex; gap: 8px; padding: 8px; border: 1px solid rgba(191, 169, 139, 0.72); border-radius: 999px; background: rgba(255, 250, 245, 0.8); box-shadow: 0 10px 24px rgba(106, 75, 41, 0.08); }
.lang-chip, .nav-chip, .version-tab { border-radius: 999px; cursor: pointer; }
.lang-chip { border: 1px solid transparent; background: transparent; color: #604d3d; font-family: "Cormorant Garamond", Georgia, serif; font-size: 1rem; padding: 10px 16px; transition: transform 0.22s ease, box-shadow 0.22s ease, background-color 0.22s ease, color 0.22s ease, border-color 0.22s ease; }
.lang-chip.active { background: linear-gradient(135deg, #b64926 0%, #8a2613 100%); color: #fff8f3; }
.lang-chip:hover { transform: translateY(-2px) scale(1.02); box-shadow: 0 10px 20px rgba(115, 80, 43, 0.14); }
.nav-chip { border: 1px solid rgba(177, 144, 108, 0.7); background: rgba(255, 249, 240, 0.88); color: #5b4636; padding: 10px 15px; transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease, background-color 0.22s ease, opacity 0.22s ease; }
.nav-chip.subtle { background: rgba(245, 236, 223, 0.95); }
.nav-chip:hover, .version-tab:hover, .clickable-card:hover { border-color: rgba(184, 94, 55, 0.62); box-shadow: 0 12px 24px rgba(105, 74, 40, 0.12); }
.panel-card, .state-card, .error-panel, .section-card, .term-card, .media-card { border: 1px solid var(--line); border-radius: 28px; background: linear-gradient(180deg, var(--paper-strong) 0%, var(--paper) 100%); box-shadow: 0 22px 44px rgba(118, 87, 49, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.75); }
.hero-card, .media-card, .version-card-wrap, .term-card-wrap, .state-card, .error-panel { padding: 20px 22px 22px; }
.hero-card { display: grid; grid-template-columns: 1.35fr 0.85fr; gap: 18px; }
.eyebrow, .section-tag { margin: 0 0 10px; font-family: "Cormorant Garamond", "Times New Roman", serif; font-size: 14px; letter-spacing: 0.2em; text-transform: uppercase; color: #9c341d; }
.hero-card h1, .section-head h2, .section-card h3, .term-card h3 { margin: 0; color: var(--ink); }
.hero-card h1 { font-family: "Noto Serif JP", "Yu Mincho", Georgia, serif; font-size: clamp(2.4rem, 4.2vw, 4rem); line-height: 1.1; }
.lead, .hero-meta, .section-card p, .term-card p, .section-head span, .state-card, .error-panel { color: var(--ink-soft); line-height: 1.8; }
.hero-meta { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; align-content: start; }
.meta-box { border: 1px solid rgba(216, 180, 139, 0.58); border-radius: 20px; background: rgba(255, 248, 239, 0.82); padding: 16px; display: grid; gap: 6px; }
.meta-box.wide { grid-column: 1 / -1; }
.meta-label { font-size: 0.8rem; letter-spacing: 0.08em; text-transform: uppercase; color: #7a5d44; }
.meta-box strong { color: var(--ink); font-family: "Cormorant Garamond", Georgia, serif; font-size: 1.24rem; word-break: break-word; }
.soft-badge, .status-chip, .ink-chip { display: inline-flex; align-items: center; border-radius: 999px; padding: 6px 11px; font-size: 0.78rem; letter-spacing: 0.06em; }
.soft-badge { background: rgba(235, 220, 201, 0.9); color: #6d5138; }
.status-chip { background: linear-gradient(135deg, #442616 0%, #24130b 100%); color: #fff7f0; }
.ink-chip { background: rgba(245, 233, 216, 0.95); color: #705940; }
.video-grid, .section-list, .term-list, .split-stage { display: grid; gap: 14px; }
.video-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.split-stage { grid-template-columns: 1.15fr 0.85fr; }
.video-frame { position: relative; padding-bottom: 56.25%; overflow: hidden; border-radius: 20px; border: 1px solid rgba(205, 175, 140, 0.75); background: #1a120a; }
.video-frame iframe { position: absolute; inset: 0; width: 100%; height: 100%; border: 0; }
.version-tabs { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 14px; }
.version-tab { border: 1px solid rgba(196, 163, 129, 0.58); background: rgba(255, 247, 237, 0.95); color: #5e4837; padding: 10px 14px; display: grid; gap: 4px; text-align: left; transition: transform 0.22s ease, background-color 0.22s ease, border-color 0.22s ease, box-shadow 0.22s ease; }
.version-tab.active { background: linear-gradient(135deg, #b64926 0%, #8a2613 100%); color: #fff7f0; }
.version-tab small { font-size: 0.78rem; opacity: 0.88; }
.section-card, .term-card { padding: 16px 18px 18px; }
.clickable-card { cursor: pointer; transition: transform 0.22s ease, border-color 0.22s ease, box-shadow 0.22s ease; }
.term-pron { margin: 6px 0 0; color: #7c654c; }
.empty-state { color: var(--ink-soft); line-height: 1.8; }
@media (max-width: 1080px) { .hero-card, .split-stage, .video-grid { grid-template-columns: 1fr; } }
@media (max-width: 760px) { .atlas-page { padding: 14px; } .topbar { flex-direction: column; align-items: flex-start; } .lang-pill { align-self: flex-start; } .hero-meta { grid-template-columns: 1fr; } }
</style>
